import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import EmbedPage from '../pages/EmbedPage';
import { ensureNcSession, getGatewayUserId, getNcSsoUrl } from '../lib/ncSso';
import {
  causeText, claimNcReload, classifyEmbedError, currentNcRecovery, detectFrameProblem, embedErrorReason,
  probeMmState, probeNcState, reauthGuard, relogin, reportEmbedAuthEvent, resetNcRecovery, subscribeNcRecovered,
  trackNcRecovery,
} from '../lib/embedAuth';
import { isEmbedSplitApp, useSplitView } from './SplitShell';
import { requestEmbedAuth, requestEmbedReload } from '../utils/embedReload';
import { countVisible, findNestedAppMenu, hasHidden, hideOthers, pickKeeper, restoreHidden } from '../utils/sidebarDedupe';
import { withEulReul } from '../utils/koreanParticle';

export const EMBED_APPS = {
  messenger: {
    title: '메신저',
    appRoot: '/mm/ourbox',
    // 🔴 redirect_to 는 MM 이 자기 SiteURL 서브패스(/mm) 뒤에 이어 붙여 해석한다 — '/mm/ourbox' 로 주면
    //    '/mm/mm/ourbox' 로 가서 팀 이름이 'mm' 으로 읽히고 「팀을 찾을 수 없습니다」가 뜬다(2026-09-22 nginx 로그 확인).
    ssoUrl: '/mm/oauth/gitlab/login?redirect_to=%2Fourbox',
    // 초대 항목 숨김 — 메신저는 게이트웨이 가입자만 쓴다(관리자 포함 전원). 사이드바 «회원 초대» =
    // #inviteMembersButton, «+» 메뉴 «초대하기» = #invitePeopleMenuItem(권한을 빼도 남고 누르면 거부 모달만 뜬다).
    hideCss: '#initialPageLoadingScreen{display:none !important}[class*="ProductMenuContainer"]{display:none !important}button.HeaderIconButton[aria-label="설정"]{display:none !important}ul#userAccountMenu>li:last-child{display:none !important}#inviteMembersButton,#invitePeopleMenuItem{display:none !important}',
  },
  drive: {
    title: '드라이브',
    appRoot: '/drive/apps/files/',
    redirectUrl: '/drive/apps/files/',
  },
  calendar: {
    title: '캘린더',
    appRoot: '/drive/apps/calendar/',
    redirectUrl: '/drive/apps/calendar/',
  },
  mail: {
    title: '메일',
    appRoot: '/drive/apps/mail/',
    redirectUrl: '/drive/apps/mail/',
  },
  mail2: {
    // 라벨은 '메일'(Roundcube 로 일원화, 2026-08-24). key 'mail2' 는 권한·라우트가 묶여 있어 유지.
    title: '메일',
    appRoot: '/mail2/',
  },
  talk: {
    title: '화상회의',
    appRoot: '/drive/apps/spreed/',
    redirectUrl: '/drive/apps/spreed/',
  },
};

const NC_EMBED_APPS = new Set(['drive', 'calendar', 'mail', 'talk']);

const PUBLIC_PATHS = new Set([
  '/login',
  '/register',
  '/verify-email',
  '/unlock-account',
  '/forgot-password',
  '/reset-password',
  '/authorize',
]);

const EmbedHostContext = createContext(null);

// ── 임베드 인증 만료 자동 복구(docs/embed_auth_expiry.md ⑥) ──
const AUTH_PROBE_MIN_MS = 30000;         // 서비스별 세션 확인(probe)은 30초에 한 번까지
const AUTH_RESUME_DEBOUNCE_MS = 300;     // visibilitychange·pageshow 를 한 번으로 묶는다
const LOGIN_BOUNCE_MIN_MS = 5000;        // 로그인 화면이 5초 넘게(주기 검사 2회 연속) 남아 있을 때만 확인 요청

const GATE_LOADING = { loading: true, error: false };
const GATE_IDLE = { loading: false, error: false };

function sameUid(a, b) {
  return a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);
}

// 게이트 값이 같은가(없음 = 대기 없음·오류 없음). 같으면 상태를 바꾸지 않는다(다시 그리지 않는다).
function sameGate(a, b) {
  const x = a || GATE_IDLE;
  const y = b || GATE_IDLE;
  return !!x.loading === !!y.loading && !!x.error === !!y.error && (x.cause || null) === (y.cause || null);
}

function getVisibleAppHeight() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  try {
    const rootStyle = getComputedStyle(document.documentElement);
    const zoomValue = parseFloat(rootStyle.zoom);
    const zoom = Number.isFinite(zoomValue) && zoomValue > 0 ? zoomValue : 1;
    const viewportHeight = window.visualViewport?.height;
    if (Number.isFinite(viewportHeight) && viewportHeight > 0) return viewportHeight / zoom;

    const appHeight = parseFloat(rootStyle.getPropertyValue('--gw-app-h'));
    return Number.isFinite(appHeight) && appHeight > 0 ? appHeight : null;
  } catch (e) {
    return null;
  }
}

export function EmbedHostProvider({ children }) {
  const location = useLocation();
  const [visited, setVisited] = useState([]);
  const [active, setActive] = useState(null);
  const [ncGate, setNcGate] = useState({});
  const ncConnectRef = useRef({});
  const resetSeqRef = useRef(0);
  // 방문한 앱(state 는 렌더 뒤에야 바뀐다 — «호출 시점에 이미 떠 있던 앱인가» 는 이 ref 로 본다).
  const visitedRef = useRef([]);
  // 이 탭에서 NC 세션을 한 번이라도 확인했는가 — 첫 진입 SSO 를 '만료' 로 세지 않는다.
  const ncSeenRef = useRef(false);
  const probeAtRef = useRef({});           // 서비스별 마지막 probe 시각(30초에 한 번까지)
  const ncRecoverRef = useRef(null);       // 복귀 확인의 NC 재인증 — 겹쳐 돌지 않게
  // 숨은 SSO 로 세션이 바뀐 뒤 아직 다시 불러오지 않은 방문 NC 앱 → 그 복구(약속). 보이는 앱은 그 자리에서,
  // 숨은 앱은 다시 열 때 한 번 다시 불러온다(claimNcReload — 화면이 먼저 했으면 건너뛴다).
  const ncStaleRef = useRef(new Map());

  const markVisited = useCallback((app) => {
    if (!visitedRef.current.includes(app)) visitedRef.current = [...visitedRef.current, app];
    setVisited((current) => (current.includes(app) ? current : [...current, app]));
  }, []);

  // 🔴 게이트는 값이 실제로 바뀔 때만 바꾼다 — 같은 값으로 새 객체를 넣으면 셸 전체가 다시 그려진다.
  const setGates = useCallback((apps, gate) => {
    if (!apps || apps.length === 0) return;
    setNcGate((current) => {
      let next = current;
      apps.forEach((app) => {
        if (sameGate(current[app], gate)) return;
        if (next === current) next = { ...current };
        next[app] = gate;
      });
      return next;
    });
  }, []);

  const markNcStale = useCallback((promise) => {
    visitedRef.current.forEach((app) => {
      if (NC_EMBED_APPS.has(app)) ncStaleRef.current.set(app, promise);
    });
  }, []);

  const reloadStaleNc = useCallback((apps, reason) => {
    apps.forEach((app) => {
      if (!ncStaleRef.current.has(app)) return;
      const promise = ncStaleRef.current.get(app);
      ncStaleRef.current.delete(app);
      if (claimNcReload(promise, app)) requestEmbedAuth(app, 'reload', reason);
    });
  }, []);

  // 누가 돌린 복구든(화면의 재인증 포함) 숨은 SSO 로 세션이 새로 생기면 그때 떠 있던 NC 문서는 옛 문서다.
  useEffect(() => subscribeNcRecovered((promise) => { markNcStale(promise); }), [markNcStale]);

  const connectNcApp = useCallback((app, makeActive) => {
    const config = EMBED_APPS[app];
    if (!NC_EMBED_APPS.has(app) || !config) {
      markVisited(app);
      if (makeActive) setActive(app);
      return;
    }
    if (makeActive) setActive(app);
    setGates([app], GATE_LOADING);
    if (!ncConnectRef.current[app]) {
      const seq = resetSeqRef.current;
      const wasVisited = visitedRef.current.includes(app);
      const options = { expectedUid: getGatewayUserId(), redirectUrl: config.redirectUrl || config.appRoot };
      // 🔴 첫 진입 SSO 는 만료가 아니다 — 이 탭에서 NC 세션을 확인한 적 있을 때만 '재인증 시작' 을 보고한다.
      //    (그 전에는 옵션을 위의 둘만 넘긴다. r6_472fdc3e1e·r6_4a376d9a3c 가 호출 인자를 통째로 본다.)
      if (ncSeenRef.current) options.onReauthStart = () => { reportEmbedAuthEvent('nextcloud', 'expired', 'open_check'); };
      // 🔴 열기 직전 확인도 NC 복구로 기록한다 — 도는 동안 화면(EmbedPage)·복귀 확인·5초 감시는 새 복구를
      //    띄우지 않고 이 결과를 기다린다. 목킹된 ensureNcSession 은 undefined 를 돌려줄 수 있다(trackNcRecovery 가 감싼다).
      const recovery = trackNcRecovery(ensureNcSession(options));
      const promise = recovery
        .then((session) => {
          if (seq !== resetSeqRef.current) return;
          ncSeenRef.current = true;
          if (session && session.reauthed) {
            reauthGuard.succeed('nextcloud');
            reportEmbedAuthEvent('nextcloud', 'reauth_ok', 'open_check');
            markNcStale(recovery);
          }
          markVisited(app);
          setGates([app], GATE_IDLE);
          // 이미 떠 있던 iframe 이 옛 NC 문서면(숨은 SSO 로 쿠키가 바뀌었다 — 이번이든 앞선 복구든) 한 번 다시 불러온다.
          if (wasVisited) reloadStaleNc([app], 'open_check');
        })
        .catch((err) => {
          if (seq !== resetSeqRef.current) return;
          reportEmbedAuthEvent('nextcloud', 'reauth_fail', embedErrorReason(err));
          setGates([app], { loading: false, error: true, cause: classifyEmbedError(err) });
        })
        .finally(() => {
          if (ncConnectRef.current[app] === promise) delete ncConnectRef.current[app];
        });
      ncConnectRef.current[app] = promise;
    }
  }, [markVisited, setGates, markNcStale, reloadStaleNc]);

  const activate = useCallback((app) => {
    if (!EMBED_APPS[app]) return;
    connectNcApp(app, true);
  }, [connectNcApp]);

  const ensureVisited = useCallback((app) => {
    if (!EMBED_APPS[app]) return;
    connectNcApp(app, false);
  }, [connectNcApp]);

  const deactivate = useCallback(() => {
    setActive(null);
  }, []);

  const reset = useCallback(() => {
    resetSeqRef.current += 1;
    ncConnectRef.current = {};
    visitedRef.current = [];
    ncSeenRef.current = false;
    probeAtRef.current = {};
    ncStaleRef.current = new Map();
    reauthGuard.resetAll();
    resetNcRecovery();
    setVisited([]);
    setActive(null);
    setNcGate({});
  }, []);

  useEffect(() => {
    if (PUBLIC_PATHS.has(location.pathname) || !localStorage.getItem('token')) {
      reset();
    }
  }, [location.pathname, reset]);

  // 복귀(탭 다시 보임·bfcache 복원) 때 방문한 임베드의 세션을 미리 확인한다. shownApps = 지금 보이는 앱들.
  // 🔴 정상 세션이면 아무것도 하지 않는다(SSO·재로드 0 — 화상회의 통화 보호). 방문하지 않은 앱은 건드리지 않는다
  //    (부하) — 그런 앱은 열 때 connectNcApp(열기 직전 확인)이 처리한다.
  const resumeAuthCheck = useCallback((shownApps, reason) => {
    const why = reason || 'visibility_check';
    const shown = (Array.isArray(shownApps) ? shownApps : [])
      .filter((app, i, arr) => app && EMBED_APPS[app] && arr.indexOf(app) === i);
    const visitedNow = visitedRef.current;
    const now = Date.now();
    const due = (service) => {
      const last = probeAtRef.current[service] || 0;
      if (last && now - last < AUTH_PROBE_MIN_MS) return false;
      probeAtRef.current[service] = now;
      return true;
    };

    // Nextcloud — 숨은 SSO 로 쿠키를 되살리고, 방문한 NC 앱의 옛 문서를 다시 불러온다.
    // 🔴 이미 NC 복구가 돌고 있으면(열기 직전 확인·화면의 재인증) 확인도 새 복구도 하지 않는다 — 그 결과가 답이고,
    //    옛 문서는 그 복구 뒤 보이는 화면·다시 열 때 한 번 다시 불러온다(ncStaleRef).
    if (visitedNow.some((app) => NC_EMBED_APPS.has(app)) && !ncRecoverRef.current && !currentNcRecovery() && due('nextcloud')) {
      const seq = resetSeqRef.current;
      const run = probeNcState().then((probe) => {
        if (seq !== resetSeqRef.current) return undefined;
        const expectedUid = getGatewayUserId();
        if (probe.state === 'unreachable') return undefined;   // 일시 장애를 미리 경고하지 않는다
        if (probe.state === 'ok' && (!expectedUid || sameUid(probe.uid, expectedUid))) {
          ncSeenRef.current = true;
          return undefined;
        }
        if (currentNcRecovery()) return undefined;              // 확인하는 사이 다른 길이 복구를 시작했다
        if (!reauthGuard.tryAcquire('nextcloud')) {
          reportEmbedAuthEvent('nextcloud', 'expired', 'cooldown');
          return undefined;
        }
        reportEmbedAuthEvent('nextcloud', 'expired', why);
        const shownNc = shown.filter((app) => NC_EMBED_APPS.has(app) && visitedRef.current.includes(app));
        setGates(shownNc, GATE_LOADING);
        const recovery = trackNcRecovery(ensureNcSession({ expectedUid, redirectUrl: EMBED_APPS.drive.redirectUrl }));
        return recovery.then(() => {
          if (seq !== resetSeqRef.current) return;
          ncSeenRef.current = true;
          reauthGuard.succeed('nextcloud');
          // 확인이 '세션 없음' 이었다 — 방문한 NC 문서는 전부 옛 문서다(다시 불러오기는 앱마다 한 번).
          markNcStale(recovery);
          reloadStaleNc(visitedRef.current.filter((app) => NC_EMBED_APPS.has(app)), why);
          setGates(shownNc, GATE_IDLE);
          reportEmbedAuthEvent('nextcloud', 'reauth_ok', why);
        }, (err) => {
          if (seq !== resetSeqRef.current) return;
          // 🔴 자동 재인증이 실패하면 자동으로 다시 시도하지 않는다 — «다시 시도» 만 guard 를 푼다.
          reauthGuard.fail('nextcloud');
          setGates(shownNc, { loading: false, error: true, cause: classifyEmbedError(err) });
          reportEmbedAuthEvent('nextcloud', 'reauth_fail', embedErrorReason(err));
        });
      }).catch(() => {}).finally(() => {
        if (ncRecoverRef.current === run) ncRecoverRef.current = null;
      });
      ncRecoverRef.current = run;
    }

    // Mattermost — 세션이 만료됐을 때만 그 화면에 재인증을 맡긴다.
    if (visitedNow.includes('messenger') && due('mattermost')) {
      probeMmState().then((state) => {
        if (state === 'expired') requestEmbedAuth('messenger', 'reauth', why);
      }).catch(() => {});
    }

    // Roundcube — 로그인 화면이 떠 있는지는 그 화면만 안다(경로가 `/mail2/` 그대로다).
    if (visitedNow.includes('mail2') && due('roundcube')) {
      requestEmbedAuth('mail2', 'check', why);
    }
  }, [setGates, markNcStale, reloadStaleNc]);

  const value = useMemo(() => ({ visited, active, ncGate, activate, ensureVisited, deactivate, reset, resumeAuthCheck }), [visited, active, ncGate, activate, ensureVisited, deactivate, reset, resumeAuthCheck]);

  return (
    <EmbedHostContext.Provider value={value}>
      {children}
    </EmbedHostContext.Provider>
  );
}

function useEmbedHost() {
  const context = useContext(EmbedHostContext);
  if (!context) throw new Error('EmbedHostProvider is required');
  return context;
}

export function EmbedActivator({ app }) {
  const { activate, deactivate } = useEmbedHost();

  useEffect(() => {
    activate(app);
    return () => deactivate();
  }, [app, activate, deactivate]);

  return null;
}

// ── 앱 메뉴(사이드바) 두 벌 표시 자가 정상화(마스터 지시 2026-09-28: 원인 규명보다 자가 복구) ──
// 🔴 최상위 새로고침은 쓰지 않는다 — 녹음·작성 중 입력·다른 임베드를 잃는다.
const DEDUPE_AFTER_CHANGE_MS = [300, 1200];   // 상태 변경 뒤 확인 시점
const DEDUPE_CONFIRM_MS = 150;                // 두 번 «연속»일 때만 조치
const DEDUPE_REMEASURE_MS = 100;              // resize 재측정 뒤 다시 셀 때까지
const DEDUPE_RESIZE_DEBOUNCE_MS = 300;
const DEDUPE_POLL_MS = 5000;                  // 보이는 동안 주기 검사(복원은 하지 않는다)
const NESTED_COOLDOWN_MS = 30000;             // 같은 iframe 재로드 최소 간격
const NESTED_WINDOW_MS = 600000;              // 10분
const NESTED_MAX_IN_WINDOW = 3;

export function EmbedHost() {
  const { visited, active, ncGate, activate, ensureVisited, resumeAuthCheck } = useEmbedHost();
  const { sideApp, slotRects } = useSplitView();
  const location = useLocation();
  // 대기(setTimeout·rAF) 뒤 콜백이 옛 값을 쓰지 않도록 최신 상태는 ref 로 읽는다.
  const activeRef = useRef(active);
  const sideAppRef = useRef(sideApp);
  const pathRef = useRef(location.pathname);
  const dedupeGenRef = useRef(0);          // 세대 — 상태가 바뀌면 옛 대기는 아무것도 하지 않는다
  const dedupeEpisodeRef = useRef(null);   // 이상 상태(episode) — resize·warn 은 episode 당 1회
  const nestedStateRef = useRef({});       // app 별 재로드 이력(30초 쿨다운·10분 3회 상한)
  const loginBounceRef = useRef({});       // app 별 로그인 화면 관측 { since, fired } — 사라지면 지운다
  const ncGateRef = useRef(ncGate);        // 5초 감시가 읽는 최신 게이트(연결 중인 앱은 감시하지 않는다)

  useEffect(() => { ncGateRef.current = ncGate; }, [ncGate]);

  useEffect(() => {
    if (isEmbedSplitApp(sideApp)) ensureVisited(sideApp);
  }, [ensureVisited, sideApp]);

  // 복귀 신호: 탭이 다시 보임(visibilitychange) · bfcache 복원(pageshow persisted). 모바일은 PWA/브라우저라
  // 이 둘이 기준 신호다. 300ms 로 묶고, 로그인 안 된 화면에서는 아무것도 하지 않는다.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || typeof resumeAuthCheck !== 'function') return undefined;
    let timer = null;
    const fire = () => {
      timer = null;
      if (document.visibilityState !== 'visible') return;
      let token = null;
      try { token = localStorage.getItem('token'); } catch (e) { token = null; }
      if (!token || PUBLIC_PATHS.has(pathRef.current)) return;
      const main = activeRef.current;
      const side = sideAppRef.current && sideAppRef.current !== main ? sideAppRef.current : null;
      resumeAuthCheck([main, side], 'visibility_check');
    };
    const schedule = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(fire, AUTH_RESUME_DEBOUNCE_MS);
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') schedule(); };
    const onPageShow = (e) => { if (e && e.persisted) schedule(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [resumeAuthCheck]);

  useEffect(() => {
    activeRef.current = active;
    sideAppRef.current = sideApp;
    pathRef.current = location.pathname;
    dedupeGenRef.current += 1;
    dedupeEpisodeRef.current = null;
    const gen = dedupeGenRef.current;
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    let disposed = false;
    const timers = new Set();
    const frames = new Set();
    const alive = () => !disposed && gen === dedupeGenRef.current;   // 세대가 다르면 아무것도 안 한다
    const later = (fn, ms) => {
      const id = setTimeout(() => { timers.delete(id); if (alive()) fn(); }, ms);
      timers.add(id);
      return id;
    };
    const frame = (fn) => {
      if (typeof window.requestAnimationFrame !== 'function') { later(fn, 0); return; }
      const id = window.requestAnimationFrame(() => { frames.delete(id); if (alive()) fn(); });
      frames.add(id);
    };
    // 숨은 탭은 rAF 가 멈춰 슬롯 측정이 밀린다 → 오탐 방지로 아무 검사도 하지 않는다.
    const shown = () => document.visibilityState === 'visible';
    const menus = () => { const r = countVisible(document, window); return Array.isArray(r) ? r : []; };

    const warnOnce = (episode, kind, before, after) => {
      if (!episode || episode.warned) return;
      episode.warned = true;
      try {
        console.warn('[gw] 앱 메뉴 중복 표시 감지 → 정상화', {
          kind, path: pathRef.current, activeEmbedApp: activeRef.current, sideApp: sideAppRef.current, before, after,
        });
      } catch (e) { /* */ }
    };

    // 1-A. 같은 문서 안에서 두 벌 — keeper 만 남기고 나머지를 숨긴다.
    const hideDuplicates = (els) => {
      const before = els.length;
      const keeper = pickKeeper(els, activeRef.current);
      if (!keeper) return;
      const others = els.filter((el) => el && el !== keeper);
      if (others.length === 0) return;
      hideOthers(others);
      warnOnce(dedupeEpisodeRef.current, 'same-doc', before, menus().length);
    };

    const confirmSameDoc = () => later(() => {
      const second = menus();
      if (second.length <= 1) { dedupeEpisodeRef.current = null; return; }
      if (!dedupeEpisodeRef.current) dedupeEpisodeRef.current = { resized: false, warned: false };
      const episode = dedupeEpisodeRef.current;
      if (episode.resized) { hideDuplicates(second); return; }
      episode.resized = true;                      // episode 당 재측정 요청 1회
      try { window.dispatchEvent(new Event('resize')); } catch (e) { /* */ }
      frame(() => later(() => {
        const third = menus();
        if (third.length <= 1) { dedupeEpisodeRef.current = null; return; }
        hideDuplicates(third);
      }, DEDUPE_REMEASURE_MS));
    }, DEDUPE_CONFIRM_MS);

    const embedIframes = () => {
      const out = [];
      ['main', 'side'].forEach((slot) => {
        let host = null;
        try { host = document.querySelector('[data-gw-embed-slot="' + slot + '"]'); } catch (e) { host = null; }
        if (!host) return;
        const app = typeof host.getAttribute === 'function' ? host.getAttribute('data-gw-embed-app') : null;
        let iframe = null;
        try { iframe = host.querySelector('iframe'); } catch (e) { iframe = null; }
        if (app && iframe) out.push({ app, iframe });
      });
      return out;
    };

    // 1-B. iframe 안에 게이트웨이가 다시 떴다 — 기존 재로드 경로로 그 iframe 한 장만 되돌린다.
    const reloadNested = (item) => {
      const now = Date.now();
      const state = nestedStateRef.current[item.app]
        || (nestedStateRef.current[item.app] = { last: 0, times: [], warned: false });
      if (state.last && now - state.last < NESTED_COOLDOWN_MS) return;
      state.times = state.times.filter((t) => now - t < NESTED_WINDOW_MS);
      if (state.times.length >= NESTED_MAX_IN_WINDOW) { warnOnce(state, 'nested-iframe-throttled', 1, 1); return; }
      state.last = now;
      state.times.push(now);
      // 신호를 못 보냈을 때만(=false) 그 iframe 을 앱 root 로 직접 되돌린다.
      if (requestEmbedReload(item.app) !== true) {
        const root = EMBED_APPS[item.app] && EMBED_APPS[item.app].appRoot;
        try { if (root && item.iframe.contentWindow) item.iframe.contentWindow.location.replace(root); } catch (e) { /* */ }
      }
      warnOnce(state, 'nested-iframe', 1, findNestedAppMenu(item.iframe) === true ? 1 : 0);
    };

    const confirmNested = () => {
      const items = embedIframes();
      const suspects = items.filter((item) => findNestedAppMenu(item.iframe) === true);
      items.forEach((item) => {
        const state = nestedStateRef.current[item.app];
        if (state && suspects.indexOf(item) === -1) state.warned = false;   // 정상 복귀 = episode 종료
      });
      if (suspects.length === 0) return;
      later(() => {
        suspects.forEach((item) => {
          if (findNestedAppMenu(item.iframe) !== true) {
            const state = nestedStateRef.current[item.app];
            if (state) state.warned = false;
            return;
          }
          reloadNested(item);
        });
      }, DEDUPE_CONFIRM_MS);
    };

    // 1-C. 보이는 iframe 이 로그인 화면으로 튕겨 있다 — 그 화면에 확인을 맡긴다(로그인 화면일 때만 재인증).
    // 🔴 5초 넘게(주기 검사 2회 연속) 남아 있을 때만 한 번 보낸다 — SSO 중간 경로처럼 잠깐 지나가는 건 걸리지 않는다.
    // 🔴 NC 복구가 도는 동안·연결 중인 NC 앱은 세지 않는다 — 그 복구가 끝나면 이 앱을 다시 불러온다(같은 앱에
    //    복구를 하나 더 얹지 않는다). 끝난 뒤에도 로그인 화면이면 그때부터 다시 센다.
    const checkLoginBounce = () => {
      const now = Date.now();
      const seen = {};
      const ncBusy = !!currentNcRecovery();
      embedIframes().forEach((item) => {
        if (NC_EMBED_APPS.has(item.app)) {
          const gate = ncGateRef.current && ncGateRef.current[item.app];
          if (ncBusy || (gate && gate.loading)) return;
        }
        const problem = detectFrameProblem(item.iframe, item.app, { afterLoad: false });
        if (problem !== 'login_redirect' && problem !== 'gateway_login') return;
        seen[item.app] = true;
        const state = loginBounceRef.current[item.app]
          || (loginBounceRef.current[item.app] = { since: now, fired: false });
        if (state.fired || now - state.since < LOGIN_BOUNCE_MIN_MS) return;
        state.fired = true;                        // 같은 상태(episode)에서는 한 번만
        requestEmbedAuth(item.app, 'check', 'login_redirect');
      });
      Object.keys(loginBounceRef.current).forEach((app) => {
        if (!seen[app]) delete loginBounceRef.current[app];   // 정상 문서로 돌아옴 = episode 종료
      });
    };

    // 주기·resize 검사는 복원하지 않는다(깜빡임 방지). 단 보이는 메뉴가 0개인데 숨긴 게 있으면 되돌린다.
    const check = () => {
      if (!shown()) return;
      const first = menus();
      if (first.length === 0 && hasHidden(document)) restoreHidden(document);
      if (first.length <= 1) dedupeEpisodeRef.current = null;
      else confirmSameDoc();
      confirmNested();
      checkLoginBounce();
    };

    let resizeTimer = null;
    const onResize = () => {
      if (resizeTimer !== null) { clearTimeout(resizeTimer); timers.delete(resizeTimer); }
      resizeTimer = later(() => { resizeTimer = null; check(); }, DEDUPE_RESIZE_DEBOUNCE_MS);
    };
    const onVisibility = () => { if (shown()) check(); };

    restoreHidden(document);                       // 상태가 바뀌면 즉시 복원
    DEDUPE_AFTER_CHANGE_MS.forEach((ms) => later(check, ms));
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);
    const poll = setInterval(() => { if (alive()) check(); }, DEDUPE_POLL_MS);

    return () => {
      disposed = true;
      clearInterval(poll);
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
      if (typeof window.cancelAnimationFrame === 'function') frames.forEach((id) => window.cancelAnimationFrame(id));
      frames.clear();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      restoreHidden(document);                     // 언마운트 시에도 복원
    };
  }, [active, sideApp, location.pathname]);

  const getSlotStyle = (slot) => {
    const rect = slot === 'side' ? slotRects.side : slotRects.main;
    if (!slot || !rect) return { display: 'none' };
    const visibleAppHeight = getVisibleAppHeight();
    const availableHeight = visibleAppHeight === null
      ? rect.height
      : Math.max(0, visibleAppHeight - rect.top);
    const height = Math.min(rect.height, availableHeight);
    return {
      position: 'fixed',
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height,
      display: 'block',
      overflow: 'hidden',
      background: '#fff',
      zIndex: 25,
      '--gw-app-h': `${Math.round(height)}px`,
    };
  };

  const ncGatePanel = (app, slot) => {
    if (!NC_EMBED_APPS.has(app)) return null;
    const gate = ncGate[app];
    if (!gate || (!gate.loading && !gate.error)) return null;
    const config = EMBED_APPS[app];
    return (
      <div key={`nc-gate-${slot}-${app}`} style={{ ...getSlotStyle(slot), zIndex: 26 }}>
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', fontFamily: 'var(--gw-font)', color: '#6b7280', textAlign: 'center', padding: '24px', boxSizing: 'border-box' }}>
          {gate.error ? (
            <div style={{ width: 'min(420px, 100%)', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '22px', boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
              <div style={{ color: '#374151', fontSize: '15px', fontWeight: 700 }}>{config.title} 연결에 실패했습니다</div>
              <div style={{ fontSize: '13px', lineHeight: 1.6, color: '#6b7280', marginTop: '8px' }}>{withEulReul(config.title)} 열지 못했습니다.</div>
              {/* 원인 한 줄(lib/embedAuth CAUSE_TEXT) — data 속성은 e2e 가 원인 종류를 읽는 자리다. */}
              <div data-gw-embed-auth-cause={gate.cause || 'unknown'} style={{ fontSize: '12.5px', lineHeight: 1.6, color: '#9a3412', marginTop: '6px' }}>{causeText(gate.cause)}</div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '14px' }}>
                {/* 사용자가 누르는 «다시 시도» 만 자동 재인증 상한(guard)을 푼다. */}
                <button type="button" onClick={() => { reauthGuard.reset('nextcloud'); if (slot === 'main') activate(app); else ensureVisited(app); }}
                  style={{ padding: '8px 16px', background: '#111827', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>다시 시도</button>
                <button type="button" onClick={() => { relogin(); }}
                  style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>다시 로그인</button>
              </div>
            </div>
          ) : (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '18px 22px', boxShadow: '0 1px 3px rgba(15,23,42,.06)', fontSize: '13px' }}>{config.title} 연결 중…</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {visited.map((app) => {
        const config = EMBED_APPS[app];
        if (!config) return null;
        const slot = active === app ? 'main' : (sideApp === app && sideApp !== active ? 'side' : null);
        const { redirectUrl, ...embedConfig } = config;
        if (redirectUrl) embedConfig.ssoUrl = getNcSsoUrl(redirectUrl);
        return (
          <div key={app} data-gw-embed-slot={slot || 'hidden'} data-gw-embed-app={app} style={getSlotStyle(slot)}>
            <EmbedPage app={app} {...embedConfig} keepAliveActive={!!slot} splitSlot={slot || 'hidden'} />
          </div>
        );
      })}
      {active && ncGatePanel(active, 'main')}
      {sideApp && ncGatePanel(sideApp, 'side')}
    </>
  );
}
